import { Lock, Plug, Clock, CheckCircle2, AlertTriangle, ArrowRight, MinusCircle, RefreshCw } from "lucide-react";

// Honest state → visual treatment. Three key states the member must distinguish:
//   upgrade_required = plan doesn't include it (🔒 Upgrade)
//   not_connected    = paid for it, data source not linked yet (Connect)
//   not_available    = MMT legitimately has no data/integration yet (— Not Available)
const STATES = {
  upgrade_required: { label: "Upgrade Required", pill: "bg-[#b0862f]/10 text-[#b0862f] border-[#b0862f]/30", icon: Lock },
  not_connected: { label: "Not Connected", pill: "bg-blue-50 text-[#1a5fd0] border-blue-200", icon: Plug },
  connection_pending: { label: "Connection Pending", pill: "bg-blue-50 text-[#1a5fd0] border-blue-200", icon: Clock },
  connected: { label: "Connected", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  included: { label: "Included", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  not_available: { label: "Not Available", pill: "bg-slate-100 text-slate-500 border-slate-200", icon: MinusCircle },
  coming_soon: { label: "Coming Soon", pill: "bg-slate-100 text-slate-500 border-slate-200", icon: Clock },
  error: { label: "Temporarily Unavailable", pill: "bg-red-50 text-red-600 border-red-200", icon: AlertTriangle },
  stale: { label: "Update Available", pill: "bg-amber-50 text-amber-700 border-amber-200", icon: RefreshCw },
  missing: { label: "Missing", pill: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle },
  needs_attention: { label: "Needs Attention", pill: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle },
  strong: { label: "Strong", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  complete: { label: "Complete", pill: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
};

export function StatePill({ state, labelOverride }) {
  const s = STATES[state] || STATES.not_available;
  const Icon = s.icon;
  return (
    <span data-testid={`state-pill-${state}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${s.pill}`}>
      <Icon size={11} /> {labelOverride || s.label}
    </span>
  );
}

// A provider/score card that reflects membership → connection → data.
export function ProviderCard({ feature, onUpgrade, onConnect, onWaitlist, onDetails, children }) {
  const st = feature.state;
  return (
    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4" data-testid={`provider-card-${feature.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-head text-[14px] font-bold text-slate-900">{feature.name}</div>
          <div className="mt-0.5 text-[12px] text-slate-500">{feature.desc}</div>
        </div>
        <StatePill state={st} />
      </div>

      {children}

      {/* No fabricated numbers: only connected shows real data (rendered via children). */}
      {st === "upgrade_required" && (
        <button onClick={() => onUpgrade?.(feature)} data-testid={`upgrade-btn-${feature.key}`}
          className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-[#b0862f] px-4 py-2 font-head text-[13px] font-bold text-white transition-opacity hover:opacity-90">
          <Lock size={13} /> Upgrade Membership
        </button>
      )}
      {(st === "not_connected") && (
        <button onClick={() => onConnect?.(feature)} data-testid={`connect-btn-${feature.key}`}
          className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-[#1a5fd0] px-4 py-2 font-head text-[13px] font-bold text-white hover:bg-[#1550b8]">
          <Plug size={13} /> Connect
        </button>
      )}
      {st === "connected" && (
        <button onClick={() => onDetails?.(feature)} data-testid={`details-btn-${feature.key}`}
          className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2 font-head text-[13px] font-bold text-slate-700 hover:bg-slate-50">
          View Details <ArrowRight size={13} />
        </button>
      )}
      {st === "coming_soon" && (
        <button onClick={() => onWaitlist?.(feature)} data-testid={`waitlist-btn-${feature.key}`}
          className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2 font-head text-[13px] font-bold text-slate-700 hover:bg-slate-50">
          Join Waitlist
        </button>
      )}
      {st === "not_available" && (
        <div className="mt-3 text-[11px] text-slate-400">MMT hasn't enabled this connection yet. It will activate automatically once available.</div>
      )}
      {feature.why && (
        <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2">
          <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-slate-400">Why this matters</div>
          <p className="mt-0.5 text-[12px] leading-snug text-slate-600">{feature.why}</p>
        </div>
      )}
    </div>
  );
}
